import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingModal from '../../components/PricingModal';
import api from '../../api';
import { useConfig } from '../../context/ConfigContext';
import useCalendarEntitlement from '../../hooks/useCalendarEntitlement';

function AccountHub() {
  const navigate = useNavigate();
  const { getTenantSubdomain, currentTenant } = useConfig();
  const subdomain = getTenantSubdomain();
  const [showPricing, setShowPricing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [emailTestStatus, setEmailTestStatus] = useState(null);
  const [emailTestLoading, setEmailTestLoading] = useState(false);

  // The weekly auction digest opt-in. Per admin user, not per tenant: it is an
  // email preference and email arrives at a person, so one admin turning it on
  // must not subscribe every colleague. See migration 062.
  const calendarAccess = useCalendarEntitlement();
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestError, setDigestError] = useState('');

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    fetchUsageStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read unconditionally, not only when entitled. The toggle has to be able to
  // show its true state the moment the card renders, and a tenant downgraded
  // mid-week still needs to see that the email is on so they can turn it off.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/admin/calendar-digest');
        if (!cancelled) setDigestEnabled(data.digestEnabled === true);
      } catch {
        // Leave it off. A preference that cannot be read is not a reason to
        // break the account page around it.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDigestToggle = async (next) => {
    setDigestError('');
    setDigestSaving(true);

    // Optimistic, then reconciled against what the server actually stored. A
    // checkbox that does not move until a round trip finishes reads as broken.
    setDigestEnabled(next);
    try {
      const { data } = await api.patch('/admin/calendar-digest', { digestEnabled: next });
      setDigestEnabled(data.digestEnabled === true);
    } catch (error) {
      setDigestEnabled(!next);
      setDigestError(
        error.response?.data?.error || 'Could not save that. Please try again.'
      );
    } finally {
      setDigestSaving(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsageData(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use fetched data or fallback to defaults
  const currentTier = usageData?.tier || 'free_trial';
  const tierLimits = usageData?.usage || {
    activeLoans: { used: 0, max: 3 },
    states: { used: 0, max: 1 },
    properties: { used: 0, max: 10 }
  };

  const getUsagePercent = (used, max) => {
    if (max === -1) return 0; // Unlimited
    return Math.min((used / max) * 100, 100);
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return 'var(--color-error)';
    if (percent >= 70) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getTierDisplayName = (tier) => {
    const names = {
      free_trial: 'Free Trial',
      starter: 'Starter',
      pro: 'Pro',
      enterprise: 'Enterprise'
    };
    return names[tier] || 'Free Trial';
  };

  const getTierDescription = (tier) => {
    if (tier === 'enterprise') {
      return "You're on the Enterprise plan with unlimited access to all features.";
    }
    if (tier === 'pro') {
      return "You're on the Pro plan. Contact us to upgrade to Enterprise for unlimited access.";
    }
    if (tier === 'starter') {
      return "You're on the Starter plan. Upgrade to Pro or Enterprise for more features.";
    }
    return "You're on the Free Trial plan. Upgrade to unlock more features and higher limits.";
  };

  const formatUsageCount = (used, max) => {
    if (max === -1) return `${used} / Unlimited`;
    return `${used} / ${max}`;
  };

  const handleTestEmail = async () => {
    setEmailTestLoading(true);
    setEmailTestStatus(null);

    try {
      const response = await api.post('/admin/test-email');
      setEmailTestStatus({
        success: true,
        message: response.data.message || 'Test email sent successfully!'
      });
    } catch (error) {
      setEmailTestStatus({
        success: false,
        message: error.response?.data?.error || 'Failed to send test email'
      });
    } finally {
      setEmailTestLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordSaving(false);
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      setPasswordSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/change-password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

      // Auto-clear success message
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header with Back Button */}
      <div className="admin-hub-header">
        <button
          onClick={() => navigate('/admin')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Dashboard
        </button>
        <h1 className="admin-page-title">Account</h1>
        <p className="admin-hub-subtitle">Subscription and billing management</p>
      </div>

      {/* Your Website URL */}
      {subdomain && (() => {
        // Use custom domain if available, otherwise subdomain
        const websiteUrl = currentTenant?.customDomain
          ? `https://${currentTenant.customDomain}`
          : `https://${subdomain}.deedstack.com`;
        const displayDomain = currentTenant?.customDomain
          ? currentTenant.customDomain
          : `${subdomain}.deedstack.com`;

        return (
          <div className="card" style={{
            marginBottom: '20px',
            padding: '15px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px',
            background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-info-bg) 100%)',
            border: '1px solid var(--color-info-border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.3rem' }}>🌐</span>
              <div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '2px' }}>Your Website</div>
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--color-brand-text)',
                    fontWeight: '600',
                    fontSize: '1.05rem',
                    textDecoration: 'none'
                  }}
                >
                  {displayDomain}
                </a>
              </div>
            </div>
            <button
              onClick={() => window.open(websiteUrl, '_blank')}
              className="btn btn-secondary"
              style={{ fontSize: '0.9rem', padding: '8px 16px' }}
            >
              Visit Site
            </button>
          </div>
        );
      })()}

      {/* Current Plan Card */}
      <div className="card account-plan-card">
        <div className="account-plan-header">
          <div className="account-plan-info">
            <span className="account-plan-badge">{getTierDisplayName(currentTier)}</span>
            <h2 className="account-plan-title">Current Plan</h2>
            <p className="account-plan-description">
              {getTierDescription(currentTier)}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/admin/billing')}
            >
              Manage Billing
            </button>
            {currentTier !== 'enterprise' && (
              <button
                className="btn btn-primary account-upgrade-btn"
                onClick={() => setShowPricing(true)}
              >
                Upgrade Plan
              </button>
            )}
          </div>
        </div>

        <div className="account-usage-section">
          <h3 className="account-usage-title">Usage & Limits</h3>

          <div className="account-usage-grid">
            {/* Active Loans */}
            <div className="account-usage-item">
              <div className="account-usage-label">
                <span>Active Loans</span>
                <span className="account-usage-count">
                  {formatUsageCount(tierLimits.activeLoans.used, tierLimits.activeLoans.max)}
                </span>
              </div>
              <div className="account-usage-bar">
                <div
                  className="account-usage-fill"
                  style={{
                    width: tierLimits.activeLoans.max === -1 ? '10%' : `${getUsagePercent(tierLimits.activeLoans.used, tierLimits.activeLoans.max)}%`,
                    backgroundColor: tierLimits.activeLoans.max === -1 ? 'var(--color-success)' : getUsageColor(getUsagePercent(tierLimits.activeLoans.used, tierLimits.activeLoans.max))
                  }}
                />
              </div>
            </div>

            {/* Properties */}
            <div className="account-usage-item">
              <div className="account-usage-label">
                <span>Properties</span>
                <span className="account-usage-count">
                  {formatUsageCount(tierLimits.properties.used, tierLimits.properties.max)}
                </span>
              </div>
              <div className="account-usage-bar">
                <div
                  className="account-usage-fill"
                  style={{
                    width: tierLimits.properties.max === -1 ? '10%' : `${getUsagePercent(tierLimits.properties.used, tierLimits.properties.max)}%`,
                    backgroundColor: tierLimits.properties.max === -1 ? 'var(--color-success)' : getUsageColor(getUsagePercent(tierLimits.properties.used, tierLimits.properties.max))
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="account-plan-features">
          <h3 className="account-features-title">Included Features</h3>
          <ul className="account-features-list">
            <li><span className="account-feature-check">✓</span> Basic loan management</li>
            <li><span className="account-feature-check">✓</span> Customer portal</li>
            <li><span className="account-feature-check">✓</span> Payment tracking</li>
            <li>
              <span className={['pro', 'enterprise'].includes(currentTier) ? 'account-feature-check' : 'account-feature-x'}>
                {['pro', 'enterprise'].includes(currentTier) ? '✓' : '✗'}
              </span> Payment processing
            </li>
            <li>
              <span className={['starter', 'pro', 'enterprise'].includes(currentTier) ? 'account-feature-check' : 'account-feature-x'}>
                {['starter', 'pro', 'enterprise'].includes(currentTier) ? '✓' : '✗'}
              </span> Custom branding
            </li>
            <li>
              <span className={['pro', 'enterprise'].includes(currentTier) ? 'account-feature-check' : 'account-feature-x'}>
                {['pro', 'enterprise'].includes(currentTier) ? '✓' : '✗'}
              </span> eBay integration
            </li>
          </ul>
        </div>
      </div>

      {/* Billing History Card - Placeholder */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>Billing History</h2>
        <p style={{ color: 'var(--color-text-light)' }}>
          No billing history yet. Upgrade your plan to see invoices here.
        </p>
      </div>

      {/*
        The weekly auction digest. Rendered only for a tenant whose plan
        includes the calendar, because a toggle for an email that would never
        be sent is a toggle that reads as broken.

        The SEND side is gated independently: digestRecipientAdmins re-checks
        entitlement every Monday, so a stale opt-in left behind by a downgrade
        sends nothing regardless of what this card shows.
      */}
      {calendarAccess.ready && calendarAccess.included && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>Auction Calendar Email</h2>

          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={digestEnabled}
              disabled={digestSaving}
              onChange={e => handleDigestToggle(e.target.checked)}
              style={{ marginTop: '3px' }}
            />
            <span>
              <strong>Weekly auction digest</strong>
              <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginTop: '4px' }}>
                Every Monday morning: sales coming up in the next 30 days and any
                new lists posted in the last 7, for the states you sell in. Goes
                to your admin email address only.
              </div>
            </span>
          </label>

          {digestError && (
            <div style={{ marginTop: '12px', color: 'var(--color-error-text)' }}>
              {digestError}
            </div>
          )}
        </div>
      )}

      {/* Email Configuration Test */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>Email Configuration</h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '15px' }}>
          Test your email configuration by sending a test email to your admin email address.
        </p>

        <button
          onClick={handleTestEmail}
          disabled={emailTestLoading}
          className="btn btn-secondary"
          style={{ marginRight: '15px' }}
        >
          {emailTestLoading ? 'Sending...' : 'Send Test Email'}
        </button>

        {emailTestStatus && (
          <div
            style={{
              marginTop: '15px',
              padding: '12px 16px',
              borderRadius: '6px',
              backgroundColor: emailTestStatus.success ? '#e8f5e9' : '#ffebee',
              color: emailTestStatus.success ? '#2e7d32' : '#c62828',
              border: `1px solid ${emailTestStatus.success ? '#a5d6a7' : '#ef9a9a'}`
            }}
          >
            {emailTestStatus.success ? '✓ ' : '✗ '}
            {emailTestStatus.message}
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h2 style={{ marginBottom: '10px', color: 'var(--color-brand-text)' }}>Change Password</h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
          Update your admin account password. Minimum 8 characters required.
        </p>

        {passwordError && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              backgroundColor: 'var(--color-error-bg)',
              color: 'var(--color-error-text)',
              border: '1px solid var(--color-error-border)',
              marginBottom: '15px'
            }}
          >
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              backgroundColor: 'var(--color-success-bg)',
              color: 'var(--color-success-text)',
              border: '1px solid var(--color-success-border)',
              marginBottom: '15px'
            }}
          >
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={handlePasswordChange}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Current Password *
            </label>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 44px 10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  fontSize: '1rem',
                  minHeight: '44px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  color: 'var(--color-text-light)',
                  fontSize: '1.1rem'
                }}
                title={showPasswords.current ? 'Hide password' : 'Show password'}
              >
                {showPasswords.current ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              New Password *
            </label>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                minLength="8"
                style={{
                  width: '100%',
                  padding: '10px 44px 10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  fontSize: '1rem',
                  minHeight: '44px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  color: 'var(--color-text-light)',
                  fontSize: '1.1rem'
                }}
                title={showPasswords.new ? 'Hide password' : 'Show password'}
              >
                {showPasswords.new ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <small style={{ color: 'var(--color-text-light)', display: 'block', marginTop: '4px' }}>
              Minimum 8 characters
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Confirm New Password *
            </label>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 44px 10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  fontSize: '1rem',
                  minHeight: '44px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  color: 'var(--color-text-light)',
                  fontSize: '1.1rem'
                }}
                title={showPasswords.confirm ? 'Hide password' : 'Show password'}
              >
                {showPasswords.confirm ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={passwordSaving}
            className="btn btn-primary"
            style={{ minWidth: '180px' }}
          >
            {passwordSaving ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        currentTier={currentTier}
      />
    </div>
  );
}

export default AccountHub;
